import axios from "axios";
import {getLocalConfig, removeLocalConfig, setLocalConfig} from "./LocalStorageService";

const DEFAULT_API_BASE_URL = "/api/v1";

const getRuntimeConfig = () => {
    if (typeof window === "undefined") {
        return {};
    }

    return window.__TAPEARK_CONFIG__ || {};
};

// Callers append paths that already start with "/", so a trailing slash here
// would produce a double slash and a 404 from the API.
const stripTrailingSlashes = (value) => value.trim().replace(/\/+$/, "");

export const getApiEndpoint = () => {
    const runtimeConfig = getRuntimeConfig();
    return stripTrailingSlashes(runtimeConfig.apiEndpoint || process.env.REACT_APP_API_ENDPOINT || "");
};

export const getApiBaseUrl = () => {
    const runtimeConfig = getRuntimeConfig();
    return stripTrailingSlashes(runtimeConfig.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL);
};

export const getAuthBaseUrl = () => {
    const runtimeConfig = getRuntimeConfig();
    return stripTrailingSlashes(runtimeConfig.authBaseUrl || process.env.REACT_APP_AUTH_BASE_URL || "");
};

// Schemes that are meaningful as a place to send a reader. Everything else — javascript:, data:, blob: — either
// executes in our own page or renders content under our origin, which is what makes this a list of two rather
// than a check that the value merely parses.
const NAVIGABLE_SCHEMES = new Set(["http:", "https:"]);

/**
 * Vets a sign-out destination supplied by the API.
 *
 * The address comes from the handoff redemption response — the `portal_url` of the CustomerPortal row this
 * sign-in arrived from, which the API resolved from the request's Origin header. It is not read out of the URL
 * fragment and there is no allowlist here any more: the fragment was composable by whoever wrote the link, so
 * honouring it needed a list of permitted origins that somebody had to extend for every new customer. The value
 * is now chosen by the API from a row it already trusts for CORS, so the list has nothing left to decide.
 *
 * What remains is a guard at the point of use rather than a decision about who is allowed. `portal_url` is
 * validated as an https origin when a portal registers, but this value ends up assigned to window.location, and
 * a scheme check there costs nothing and does not depend on a rule enforced in another codebase staying enforced.
 *
 * http is accepted as well as https because the local demo portal is served from http://localhost:8080. Refusing
 * plaintext belongs at registration, where it can distinguish localhost from the internet; here it would only
 * break local development.
 *
 * @param {string} candidate - the address the API said to return to, or null/undefined for none.
 * @returns {string|null} the address when it is safe to navigate to, otherwise null.
 */
export const sanitisePortalReturnUrl = (candidate) => {
    if (!candidate || typeof candidate !== "string") return null;

    let parsed;
    try {
        parsed = new URL(candidate);
    } catch (error) {
        // Anything relative or malformed. A bare path would be safe to navigate to but is not what this carries,
        // and accepting one would hide a misconfiguration rather than surface it.
        return null;
    }

    return NAVIGABLE_SCHEMES.has(parsed.protocol) ? candidate : null;
};

export const getConfigInfo = async () => {
    try {
        const res = await axios.get('/env/config.json');
        setLocalConfig(res.data);
        return res.data.apiUrl;
    } catch (error) {
        console.error(error);
    }
};
window.addEventListener("beforeunload", (event) => {
    removeLocalConfig();
    // getConfigInfo();
    // console.log("API call before page reload");
});
window.addEventListener("unload", (event) => {
    // getConfigInfo();
    // console.log("API call after page reload");
});

export const getApiUrl = () => {
    // return getConfigInfo();
    if (!getLocalConfig()) {
        // return getConfigInfo();
        return getApiEndpoint();
    } else {
        // return getLocalConfig().apiUrl;
        return getApiEndpoint();
    }

}

export const ageing = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const filetypes = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const formats = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const ingested = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const items = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const policies = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const regions = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const servers = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const test = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const totals = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const addArkBridgeJobId = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;
export const dashboard = `${getApiEndpoint()}${getApiBaseUrl()}/dashboard/`;

export const notifications = getApiUrl() + getApiBaseUrl() + "/notifications/";
export const myFunction = (error) => {
    const x = document.getElementById("snackbarTemp");
    if (!x) return;
    let htmlContent = "";

    /*
     * One line of the banner. A value that is not a string has to be unwrapped rather than stringified:
     * the dashboard proxy hands an upstream failure back as `{error: <the upstream body>}`, so the value
     * under a key is itself an object, and String() on that reads '[object Object]' — a banner telling the
     * reader that something went wrong and nothing about what.
     */
    const appendText = (text) => {
        if (text === null || text === undefined) return;
        if (typeof text === "object") {
            const stated = text.message ?? text.error ?? text.detail;
            text = typeof stated === "string" ? stated : JSON.stringify(text);
        } else if (typeof text !== "string") {
            text = String(text);
        }
        const trimmed = text.trim();
        if (trimmed) {
            htmlContent += `<span>${trimmed}</span><br/>`;
        }
    };

    if (error?.response?.data) {
        const data = error.response.data;
        if (
            typeof data === "object" &&
            !Array.isArray(data) &&
            data !== null
        ) {
            for (let key in data) {
                if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
                const value = data[key];

                if (key === "detail" || key === "error") {
                    appendText(value);
                } else if (key === "non_field_errors") {
                    appendText(Array.isArray(value) ? value[0] : value);
                } else if (Array.isArray(value)) {
                    value.forEach(item => appendText(item));
                } else {
                    appendText(value);
                }
            }
        } else if (Array.isArray(data)) {
            data.forEach(item => appendText(item));
        }
    } else if (error?.error) {
        appendText(error.error);
    } else if (error instanceof Error) {
        appendText(error.message);
    } else if (typeof error === "string") {
        appendText(error);
    }

    if (!htmlContent) {
        return;
    }

    x.innerHTML = htmlContent;
    x.className = "show";
    setTimeout(function () {
        x.className = x.className.replace("show", "");
    }, 10000);
};